import React, { useContext, useRef, useState } from "react";
import { useEffect } from "react";
import CustomSpinner from "../../../../../components/CustomSpinner";
import Download from "../../../../../components/SVGComponents/Tools/Download";
import DropDown from "../../../../../components/SVGComponents/Tools/DropDown";
import { STUDY_DOWNLOAD_OPTIONS, USERTYPES, DOCUMENT_MODALITIES } from "../../../../../constants/Constants";
import DownloadOptions from "./DownloadOptions";
import studyviewerContext from "../../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import { getViewport } from "../../../helpers/CornerstoneViewportService";
import { t } from "i18next";
import themeContext from "../../../../../context/theme/ThemeContext";
import userContext from "../../../../../context/users/UserContext";
import { getEnv } from '../../../../../helpers/EnvHelper';

const DownloadButton = () => {
  const { theme } = useContext(themeContext);
  const { roles, privilegedFirmDoctor } = useContext(userContext);
  const { downloadStudyEnabled,showSavedReport } = useContext(studyviewerContext);
  const downloadOptionRef = useRef(null);
  const [loadingState, setLoadingState] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);
  const { activeViewportIndex, viewportSeriesData } = useContext(ViewportContext);
  const [showOption, setShowOption] = useState(false);
  // Timers live in refs, not state: a timer kept in state is read from a stale render, so a
  // hide could not cancel a pending open (and vice versa) and the menu opened late or stuck
  const openTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const downloadStudyAvailable =
    downloadStudyEnabled && getEnv("REACT_APP_PROTECTED_MODE") == "false";

  useEffect(() => () => {
    clearTimeout(openTimerRef.current);
    clearTimeout(hideTimerRef.current);
  }, []);

  const checkIfActiveViewportIsLoaded = async(e) => {
    // "Download study" zips the stored DICOM and needs no rendered image, so the menu opens
    // at once instead of waiting on the viewport: while a big study is still loading (or the
    // tab is short of memory) the active viewport has no images yet, and the menu used to
    // stay closed until it did. "Download images" appears once the viewport check resolves.
    if (downloadStudyAvailable) {
      onShowContextualMenu(e);
    }
    const viewport = await getViewport(activeViewportIndex);

    // A document series - an Encapsulated PDF (DOC) or Structured Report (SR) -
    // never creates a cornerstone viewport, since it renders the document
    // itself, so the image check below can never pass for one. Two
    // consequences, handled separately:
    //
    //  - The MENU must still open. "Download study" zips the stored DICOM
    //    objects and does not depend on a rendered viewport, so gating the whole
    //    dropdown on a loaded image stack hid the only download a PDF-only study
    //    can offer.
    //  - "Download images" must stay hidden. The cornerstone viewport is only
    //    hidden, not torn down, when a viewport switches from an image series to
    //    a document, so it can still hold the PREVIOUS series imageIds -
    //    capturing those would download an image the user is not looking at.
    const isDocumentViewport = DOCUMENT_MODALITIES.includes(
      viewportSeriesData?.[activeViewportIndex]?.modality
    );
    const hasLoadedImages =
      !isDocumentViewport && !!viewport && viewport.imageIds.length > 0;

    setShowOption(hasLoadedImages);
    // Only open from here when there is something to show, and only if the pointer is
    // still on the button: the check can resolve after the user has moved away
    if (!downloadStudyAvailable && hasLoadedImages && pointerInsideRef.current) {
      onShowContextualMenu(e);
    }
  }

  const handleDropDownClick = async (e) => {
    if(!showDropDown) {
      await checkIfActiveViewportIsLoaded(e);
    }
    //setShowDropDown(!showDropDown);
  };

  const handleMouseOver = () => {
    setHovered(true);
    // setShowDropDown(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        downloadOptionRef.current &&
        !downloadOptionRef.current.contains(event.target)
      ) {
        setShowDropDown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const hideContextMenu = () => {
    pointerInsideRef.current = false;
    clearTimeout(openTimerRef.current);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setShowDropDown(false);
    }, 300);
  };

  const onShowContextualMenu = (ev) => {
    ev?.preventDefault?.(); // don't navigate

    clearTimeout(hideTimerRef.current);
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      setShowDropDown(true);
    }, 300);
  };

  return (
    <>
      {loadingState && <CustomSpinner />}
      <div
        style={{ display: "flex", flexDirection: "column", position: "relative" }}
        onClick={handleDropDownClick}
        onMouseEnter={(e)=>{pointerInsideRef.current = true;setHovered(true);handleDropDownClick(e)}}
        onMouseLeave={(e)=>{setHovered(false);handleMouseLeave();hideContextMenu()}}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Download color={theme.palette.inactiveTool} />
          {/* <DropDown color={hovered ? "#0085FF" : "#C1C1C1"} /> */}
        </div>
        <div
          ref={downloadOptionRef}
          onMouseEnter={(e)=>{onShowContextualMenu(e);}}
          onMouseLeave={(e)=>{hideContextMenu()}}
          className="viewer-tool-more-dropdown"
          style={{
            display: `${showDropDown ? "flex" : "none"}`,
            flexDirection: "column",
            position: "absolute",
            top: "100%",
            marginTop: "4px",
            zIndex: "99999",
            right: "0",
            left: "unset",
            minWidth: "max-content",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
            borderRadius: "5px",
            paddingLeft: "10px",
            paddingRight: "20px",
            // boxShadow: "1px 1px 7px rgba(255, 255, 255, 0.15)"
          }}
        >
          {/* {STUDY_DOWNLOAD_OPTIONS.map((option, index) => {
            return ( */}
          {downloadStudyAvailable && (
            <div>
              <DownloadOptions
                option={t("button.download-study")}
                setShowDropDown={setShowDropDown}
                index={0}
                setHoveredParent={setHovered}
                setLoadingState={setLoadingState}
                showOption={true}
              />
            </div>
          )}
         {/* <div>
            <DownloadOptions
              option={t("button.download-ppt")}
              setShowDropDown={setShowDropDown}
              index={1}
              setHoveredParent={setHovered}
              setLoadingState={setLoadingState}
              showOption={showOption}
            />
          </div> */}
          <div>
            <DownloadOptions
              option={t("button.download-images")}
              setShowDropDown={setShowDropDown}
              index={2}
              setHoveredParent={setHovered}
              setLoadingState={setLoadingState}
              showOption={showOption}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadButton;
